import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Check, Lock, Truck } from "lucide-react";
import { EASE, MARK, gbp } from "@/components/common";
import { WHATSAPP_DISPLAY } from "@/data/vehicles";
import { apiFetch } from "@/api";

const STRIPE_APPEARANCE = {
  theme: "night",
  variables: {
    colorPrimary: "#ffffff",
    colorBackground: "#0a0a0c",
    colorText: "#f5f5f4",
    colorDanger: "#fca5a5",
    fontFamily: "Jost, sans-serif",
    borderRadius: "0px",
  },
};

const METHOD_LABELS = { klarna: "Klarna", afterpay_clearpay: "Clearpay", card: "Card" };

export default function RecoveryPay() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    apiFetch(`/recovery-pay/${token}`)
      .then((d) => {
        setInfo(d);
        if (d.payments_enabled && d.publishable_key) setStripePromise(loadStripe(d.publishable_key));
      })
      .catch((e) => setError(e.message));
  }, [token]);

  return (
    <div className="bg-[#050506] text-zinc-100 min-h-screen">
      <div className="vignette-overlay" />
      <header className="border-b border-white/[0.08] relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center">
          <Link to="/recovery" data-testid="pay-home-link" className="flex items-center gap-3">
            <img src={MARK} alt="Senna Prestige Travels" className="h-8 w-auto" />
            <span className="hidden sm:block text-[0.6rem] tracking-[0.42em] uppercase text-zinc-300">Senna Recovery — Secure Payment</span>
          </Link>
        </div>
      </header>
      <main className="relative z-10 max-w-2xl mx-auto px-6 py-16 md:py-24">
        {error && (
          <div className="text-center py-20">
            <p className="font-display text-3xl text-white">Link unavailable</p>
            <p className="mt-4 text-sm text-zinc-400 font-light">{error}</p>
            <p className="mt-4 text-sm text-zinc-400 font-light">Call or WhatsApp {WHATSAPP_DISPLAY} for help.</p>
          </div>
        )}
        {!error && !info && <p className="text-center text-zinc-500 font-light py-20">Loading secure payment…</p>}
        {!error && info && <ElementsShell key={token} info={info} token={token} stripePromise={stripePromise} />}
      </main>
    </div>
  );
}

const ElementsShell = ({ info, token, stripePromise }) => {
  const options = useMemo(
    () => ({ mode: "payment", amount: Math.round(info.estimated_total * 100), currency: "gbp", captureMethod: "manual", appearance: STRIPE_APPEARANCE }),
    [info.estimated_total]
  );
  if (!info.payments_enabled) return <PayForm info={info} token={token} />;
  return (
    <Elements stripe={stripePromise} options={options}>
      <PayForm info={info} token={token} />
    </Elements>
  );
};

const PayForm = ({ info, token }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [agreed, setAgreed] = useState(false);
  const [payReady, setPayReady] = useState(false);
  const [payMethod, setPayMethod] = useState("card");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const paid = ["balance_authorised", "fully_paid"].includes(info.payment_status);
  const bnpl = payMethod === "klarna" || payMethod === "afterpay_clearpay";
  const doneMethod = done?.method || info.payment_method || "card";
  const doneBnpl = doneMethod === "klarna" || doneMethod === "afterpay_clearpay";

  const pay = async () => {
    setBusy(true);
    setError("");
    try {
      if (!stripe || !elements) throw new Error("Payment form not ready — please wait a moment.");
      const { error: formError } = await elements.submit();
      if (formError) throw new Error(formError.message || "Please complete your payment details.");
      const intents = await apiFetch(`/recovery-pay/${token}/intent`, { method: "POST", body: { method: payMethod } });
      const billing = {
        name: info.customer_name || undefined,
        email: info.customer_email || undefined,
        address: { country: "GB" },
      };
      const returnUrl = `${window.location.origin}/payment-return?type=recovery&token=${token}`;

      if (intents.flow === "bnpl") {
        // Klarna / Clearpay — single full-quote authorisation; browser redirects to the provider
        const { error: bnplError } = await stripe.confirmPayment({
          elements,
          clientSecret: intents.bnpl_client_secret,
          confirmParams: { return_url: returnUrl, payment_method_data: { billing_details: billing } },
        });
        if (bnplError) throw new Error(bnplError.message || "Authorisation failed");
        return;
      }

      // Card — existing 30% deposit charged now + 70% balance authorised.
      // The Payment Element only collects the method here; the two intents keep their
      // own capture methods (deposit automatic, balance manual), so confirm them directly
      // with the collected PaymentMethod rather than via the deferred Elements config.
      const pmRes = await stripe.createPaymentMethod({ elements, params: { billing_details: billing } });
      if (pmRes.error) throw new Error(pmRes.error.message || "Payment details failed — please check and try again.");
      const pmId = pmRes.paymentMethod.id;

      const dep = await stripe.confirmCardPayment(intents.deposit_client_secret, { payment_method: pmId });
      if (dep.error) throw new Error(dep.error.message || "Deposit payment failed — no money has been taken. Please try again.");
      if (dep.paymentIntent?.status !== "succeeded") throw new Error("The deposit could not be completed — no money has been taken. Please try again.");

      const bal = await stripe.confirmCardPayment(intents.balance_client_secret, { payment_method: pmId });
      if (bal.error) throw new Error(bal.error.message || "Balance authorisation failed — please contact us before trying again.");

      const res = await apiFetch(`/recovery-pay/${token}/confirm`, {
        method: "POST",
        body: { deposit_pi: intents.deposit_pi, balance_pi: intents.balance_pi },
      });
      setDone(res);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (done || paid) {
    return (
      <motion.div data-testid="pay-success" className="text-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)] mb-8">
          <Check size={26} className="text-white" strokeWidth={1.5} />
        </span>
        <p className="kicker mb-4">Reference {info.ref}</p>
        <h1 className="font-display text-3xl sm:text-4xl text-white">Payment Secured</h1>
        <p className="mt-6 text-sm md:text-base text-zinc-400 font-light leading-relaxed max-w-md mx-auto">
          {doneBnpl
            ? `The full recovery total of ${gbp(info.estimated_total)} has been authorised with ${METHOD_LABELS[doneMethod]} — it is captured only when your recovery is completed, and released in full if it does not proceed.`
            : `Your 30% deposit of ${gbp(info.deposit_amount)} has been paid and the remaining ${gbp(info.balance_amount)} is authorised on your card — it is only captured when your recovery is completed.`}
        </p>
        <p className="mt-6 text-[0.62rem] tracking-[0.2em] uppercase text-zinc-500">Questions? Call or WhatsApp {WHATSAPP_DISPLAY}</p>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
      <p className="kicker mb-4 text-center">Recovery {info.ref}</p>
      <h1 className="font-display text-3xl sm:text-4xl text-white text-center">Review & Pay</h1>

      <div className="mt-10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] bg-white/[0.02] p-6 md:p-8">
        {[
          ["Vehicle", info.vehicle],
          ["Route", `${info.breakdown_postcode} → ${info.destination_postcode}`],
          ["Requested", `${info.recovery_date} at ${info.recovery_time}`],
          ...(info.pricing_type === "local"
            ? [["Pricing", info.pricing_label || "Local Peterborough Recovery — Fixed Rate"]]
            : [
                ["Truck travel", `${info.truck_miles} miles`],
                ["Recovery journey", `${info.recovery_miles} miles`],
                ["Rate", `${info.band_label} · £${info.rate.toFixed(2)}/mile`],
              ]),
          ["Recovery Total", gbp(info.estimated_total)],
        ].map(([l, v]) => (
          <div key={l} className="flex items-center justify-between gap-6 py-2.5 border-b border-white/[0.06]">
            <span className="text-sm text-zinc-400 font-light">{l}</span>
            <span className="text-sm text-zinc-100 font-light text-right">{v}</span>
          </div>
        ))}
        {bnpl ? (
          <div className="flex items-center justify-between gap-6 py-3">
            <span className="text-sm text-zinc-400 font-light">Authorised now with {METHOD_LABELS[payMethod]} — captured on completion</span>
            <span className="text-sm text-white font-light" data-testid="pay-bnpl-amount">{gbp(info.estimated_total)}</span>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-6 py-3 border-b border-white/[0.06]">
              <span className="text-sm text-zinc-400 font-light">Deposit due today (30%)</span>
              <span className="text-sm text-white font-light" data-testid="pay-deposit">{gbp(info.deposit_amount)}</span>
            </div>
            <div className="flex items-center justify-between gap-6 py-3">
              <span className="text-sm text-zinc-400 font-light">Balance authorised, captured on completion (70%)</span>
              <span className="text-sm text-white font-light" data-testid="pay-balance">{gbp(info.balance_amount)}</span>
            </div>
          </>
        )}
      </div>

      <button data-testid="pay-agree" onClick={() => setAgreed(!agreed)} className="mt-8 flex items-start gap-4 text-left">
        <span className={`chk mt-0.5 ${agreed ? "on" : ""}`}>{agreed && <Check size={12} />}</span>
        <span className="text-sm text-zinc-300 font-light leading-relaxed">
          I have reviewed and agree to the quoted recovery total of {gbp(info.estimated_total)}.
        </span>
      </button>

      {info.payments_enabled ? (
        <div className="mt-8">
          <span className="field-label">Payment Method</span>
          <div className="stripe-field" data-testid="pay-payment-element">
            <PaymentElement options={{ layout: "tabs" }} onChange={(e) => { setPayReady(e.complete); setPayMethod(e.value?.type || "card"); }} />
          </div>
          <p className="mt-3 text-[0.62rem] tracking-[0.12em] text-zinc-500 font-light flex items-center gap-2">
            <Lock size={12} />
            {bnpl
              ? `The full ${gbp(info.estimated_total)} is authorised with ${METHOD_LABELS[payMethod]} — captured only when the recovery is completed, and released in full if it does not proceed.`
              : `${gbp(info.deposit_amount)} is charged now. ${gbp(info.balance_amount)} is authorised only and captured when the recovery is completed — if the recovery does not proceed, the authorisation is cancelled and your provider releases the hold in line with their own timescales.`}
          </p>
        </div>
      ) : (
        <div className="mt-8 p-5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] bg-white/[0.02]">
          <p className="text-sm text-zinc-400 font-light leading-relaxed flex items-start gap-3">
            <Lock size={15} className="text-zinc-500 mt-0.5 shrink-0" />
            Online payment is being activated for this website. Please call or WhatsApp {WHATSAPP_DISPLAY} to arrange payment.
          </p>
        </div>
      )}

      {error && <p data-testid="pay-error" className="mt-5 text-xs text-red-300 font-light">{error}</p>}

      {info.payments_enabled && (
        <button
          data-testid="pay-submit"
          onClick={pay}
          disabled={busy || !agreed || !payReady}
          className="btn-primary w-full justify-center mt-8 disabled:opacity-50 disabled:pointer-events-none"
        >
          <Truck size={14} />
          {busy
            ? "Processing Securely…"
            : bnpl
              ? `Authorise ${gbp(info.estimated_total)} with ${METHOD_LABELS[payMethod]}`
              : `Pay ${gbp(info.deposit_amount)} Deposit + Authorise ${gbp(info.balance_amount)}`}
        </button>
      )}
    </motion.div>
  );
};
